'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { FileUpload } from './FileUpload';
import { Button } from './Button';
import { FileWithPreview, FormData, PricingTierId, BillingType, PRICING_TIERS, Upsell } from '@/types';
import { isValidEmail } from '@/lib/utils';
import { getCsrfToken } from '@/lib/csrf';
import { readFormDraft, writeFormDraft } from './formDraft';
import { useTranslation } from 'react-i18next';

const PdfPreview = dynamic(() => import('./PdfPreview').then(mod => ({ default: mod.PdfPreview })), {
  ssr: false,
  loading: () => <div className="flex justify-center items-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div></div>
});

interface ExtendedFormData extends FormData {
  name?: string;
  industry?: string;
  phone?: string;
  description?: string;
  tier?: PricingTierId;
  billingType?: BillingType;
  couponCode?: string;
  selectedUpsells?: string[];
}

// What the description textarea accepts. Anything loaded into it from elsewhere
// has to be held to the same ceiling, or it arrives longer than a person could
// have typed and checkout (whose Stripe metadata allows 500) rejects it.
export const DESCRIPTION_MAX_LENGTH = 200;

interface ResubscribeFields {
  email?: string;
  name?: string;
  phone?: string;
  description?: string;
  industry?: string;
}

/** The fields a resubscribing customer's saved details start the form with. */
export function resubscribePrefill(data: ResubscribeFields) {
  return {
    email: data.email || '',
    name: data.name || '',
    phone: data.phone || '',
    // Saved before the textarea limit existed, or by another route, so it may
    // be far longer than the box allows. A cut that lands inside an emoji drops
    // the orphaned half rather than submitting it as a replacement character.
    description: (data.description || '')
      .slice(0, DESCRIPTION_MAX_LENGTH)
      .replace(/[\uD800-\uDBFF]$/, ''),
    industry: data.industry || '',
  };
}

interface FormProps {
  onSubmit: (data: ExtendedFormData) => void;
  loading: boolean;
  selectedTier?: PricingTierId;
  selectedBilling?: BillingType;
  resubscribeData?: any;
  isResubscribeMode?: boolean;
}

const Form: React.FC<FormProps> = ({ onSubmit, loading, selectedTier: selectedTierProp, selectedBilling: selectedBillingProp, resubscribeData, isResubscribeMode }) => {
  const { t } = useTranslation();
  const [isClient, setIsClient] = useState(false);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [description, setDescription] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const [availableUpsells, setAvailableUpsells] = useState<Upsell[]>([]);
  const [selectedUpsells, setSelectedUpsells] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState<number | null>(null);
  const [emailError, setEmailError] = useState('');
  const [industry, setIndustry] = useState('');
  
  // Use props or default to professional
  const tier = selectedTierProp || 'professional';
  const billingType = selectedBillingProp || 'monthly';
  
  const selectedTier = PRICING_TIERS.find(t => t.id === tier) || PRICING_TIERS[1];
  
  // Whole dollars when exact, otherwise cents (LAUNCH20 on Starter = $15.20, not $15).
  const formatDollars = (cents: number) =>
    cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);

  const getPrice = () => {
    return billingType === 'yearly' ? selectedTier.priceYearly : selectedTier.priceMonthly;
  };

  // Load saved form data on mount OR resubscribe data
  useEffect(() => {
    setIsClient(true);
    
    // If resubscribe mode, use that data (takes priority)
    if (isResubscribeMode && resubscribeData) {
      const saved = resubscribePrefill(resubscribeData);
      if (saved.email) setEmail(saved.email);
      if (saved.name) setName(saved.name);
      if (saved.phone) setPhone(saved.phone);
      if (saved.description) setDescription(saved.description);
      if (saved.industry) setIndustry(saved.industry);
      return;
    }
    
    // Otherwise restore the saved draft, if one is still within its lifetime.
    // Note: files and the phone number are deliberately not persisted.
    const draft = readFormDraft();
    if (draft) {
      if (draft.email) setEmail(draft.email);
      if (draft.name) setName(draft.name);
      if (draft.description) setDescription(draft.description);
      if (draft.couponCode) setCouponCode(draft.couponCode);
      if (draft.selectedUpsells.length) setSelectedUpsells(draft.selectedUpsells);
    }
  }, [isResubscribeMode, resubscribeData]);

  // Save the draft whenever it changes
  useEffect(() => {
    if (!isClient) return;
    writeFormDraft({ email, name, description, couponCode, selectedUpsells });
  }, [email, name, description, couponCode, selectedUpsells, isClient]);

  // Fetch available upsells for the selected tier
  useEffect(() => {
    const fetchUpsells = async () => {
      try {
        const response = await fetch(`/api/get-upsells?tier=${tier}`);
        const data = await response.json();
        if (data.upsells) {
          setAvailableUpsells(data.upsells);
        }
      } catch (error) {
      }
    };
    fetchUpsells();
  }, [tier]);

  const handleUpsellToggle = (upsellId: string) => {
    setSelectedUpsells(prev => 
      prev.includes(upsellId) 
        ? prev.filter(id => id !== upsellId)
        : [...prev, upsellId]
    );
  };

  const getUpsellTotal = () => {
    return availableUpsells
      .filter(u => selectedUpsells.includes(u.id))
      .reduce((total, u) => total + u.price, 0);
  };

  const handleFilesChange = (newFiles: FileWithPreview[]) => {
    setFiles(newFiles);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= DESCRIPTION_MAX_LENGTH) {
      setDescription(e.target.value);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    try {
      const csrfToken = getCsrfToken();
      const response = await fetch('/api/validate-coupon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({ code: couponCode }),
      });
      const data = await response.json();
      if (data.valid) {
        setCouponStatus('valid');
        setDiscountPercent(Number(data.discountPercent) || 0);
      } else {
        setCouponStatus('invalid');
        setDiscountPercent(0);
      }
    } catch {
      setCouponStatus('invalid');
      setDiscountPercent(0);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !isValidEmail(email)) {
      setEmailError(t('pageContent.home.uploadSection.form.errors.invalidEmail'));
      return;
    }

    if (files.length === 0 && !(isResubscribeMode && resubscribeData?.hasPdf)) {
      alert(t('pageContent.home.uploadSection.form.errors.noFiles'));
      return;
    }

    onSubmit({
      email,
      name: name.trim() || undefined,
      phone: phone.trim() || undefined,
      description: description.trim() || undefined,
      tier,
      billingType,
      // A typed code counts even if "Apply" was never clicked; the server re-validates it.
      couponCode: couponStatus !== 'invalid' && couponCode.trim() ? couponCode.trim() : undefined,
      selectedUpsells: selectedUpsells.length > 0 ? selectedUpsells : undefined,
      files,
    });
  };

  const handleShowPreview = (index: number) => {
    setShowPreview(index);
  };

  const handleClosePreview = () => {
    setShowPreview(null);
  };

  if (!isClient) {
    return (
      <div className="space-y-8">
        <div className="space-y-6">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-32 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Selected Plan Summary */}
      <div className="bg-primary-50 rounded-lg p-4 border border-primary-200">
        <div className="flex justify-between items-center">
          <div>
            <span className="font-semibold text-primary-700">{selectedTier.name}</span>
            <span className="text-gray-600 ml-2">• {selectedTier.vcCount} VCs</span>
          </div>
          <div className="text-right">
            {couponStatus === 'valid' && discountPercent > 0 ? (
              <>
                <span className="text-lg text-gray-400 line-through mr-2">
                  ${(getPrice() / 100).toFixed(0)}
                </span>
                <span className="text-2xl font-bold text-green-600">
                  ${formatDollars(Math.round(getPrice() * (1 - discountPercent / 100)))}
                </span>
                <span className="ml-2 inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">
                  {discountPercent}% off
                </span>
              </>
            ) : (
              <span className="text-2xl font-bold text-primary-600">
                ${(getPrice() / 100).toFixed(0)}
              </span>
            )}
            <span className="text-gray-600 text-sm ml-1">
              {billingType === 'yearly' ? '/year' : '/mo'}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Name Field (Optional) */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pageContent.pricing.form.name')}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('pageContent.pricing.form.namePlaceholder')}
            maxLength={255}
            className="input"
          />
        </div>

        {/* Email Field (Required) */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pageContent.home.uploadSection.form.email')} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={handleEmailChange}
            placeholder={t('pageContent.home.uploadSection.form.emailPlaceholder')}
            className={`input ${emailError ? 'border-red-500 focus:ring-red-500' : ''}`}
            required
          />
          {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
        </div>

        {/* Phone Field (Optional) */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pageContent.pricing.form.phone')}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={t('pageContent.pricing.form.phonePlaceholder')}
            maxLength={50}
            className="input"
          />
        </div>

        {/* Quick Description (Optional) */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pageContent.pricing.form.description')}
          </label>
          <textarea
            id="description"
            name="description"
            value={description}
            onChange={handleDescriptionChange}
            placeholder={t('pageContent.pricing.form.descriptionPlaceholder')}
            rows={2}
            className="input"
            maxLength={DESCRIPTION_MAX_LENGTH}
          />
          <p className="mt-1 text-xs text-gray-500 text-right">
            {description.length}/{DESCRIPTION_MAX_LENGTH} {t('pageContent.home.uploadSection.form.characters')}
          </p>
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('pageContent.home.uploadSection.form.files')} {!(isResubscribeMode && resubscribeData?.hasPdf) && <span className="text-red-500">*</span>}
          </label>
          {isResubscribeMode && resubscribeData?.hasPdf && files.length === 0 && (
            <div className="mb-3 bg-green-50 border border-green-200 rounded p-3">
              <p className="text-sm text-green-800">
                ✓ Your existing PDF will be used. Upload a new one to replace it (optional).
              </p>
            </div>
          )}
          <FileUpload
            onFilesChange={handleFilesChange}
            files={files}
            maxFiles={2}
            maxSizeMB={10}
            allowedTypes={['application/pdf']}
          />
          {isResubscribeMode && resubscribeData?.hasPdf && files.length > 0 && (
            <p className="mt-2 text-sm text-yellow-700">
              ⚠️ Uploading a new file will replace your existing pitch deck.
            </p>
          )}
        </div>

        {files.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">{t('pageContent.home.uploadSection.form.preview')}:</p>
            <div className="flex flex-wrap gap-2">
              {files.map((file, index) => (
                <Button
                  key={index}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleShowPreview(index)}
                >
                  {t('pageContent.home.uploadSection.form.previewFile')} {file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Coupon Code */}
        <div>
          <label htmlFor="coupon" className="block text-sm font-medium text-gray-700 mb-1">
            {t('pageContent.pricing.form.coupon')}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="coupon"
              name="coupon"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                setCouponStatus('idle');
                setDiscountPercent(0);
              }}
              placeholder={t('pageContent.pricing.form.couponPlaceholder')}
              className={`input flex-1 ${
                couponStatus === 'valid' ? 'border-green-500' : 
                couponStatus === 'invalid' ? 'border-red-500' : ''
              }`}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleApplyCoupon}
              disabled={!couponCode.trim()}
            >
              {t('pageContent.pricing.form.couponApply')}
            </Button>
          </div>
          {couponStatus === 'valid' && (
            <p className="mt-1 text-sm text-green-600">{t('pageContent.pricing.form.couponValid')}</p>
          )}
          {couponStatus === 'invalid' && (
            <p className="mt-1 text-sm text-red-500">{t('pageContent.pricing.form.couponInvalid')}</p>
          )}
        </div>

        {/* Upsells Section */}
        {availableUpsells.length > 0 && (
          <div className="border-t pt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              {t('pageContent.pricing.upsells.title')}
            </h3>
            <div className="space-y-3">
              {availableUpsells.map((upsell) => (
                <label
                  key={upsell.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedUpsells.includes(upsell.id)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedUpsells.includes(upsell.id)}
                    onChange={() => handleUpsellToggle(upsell.id)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900">
                        {t(`pageContent.pricing.upsells.${upsell.id}.name`, upsell.name)}
                      </span>
                      <span className="font-semibold text-primary-600">
                        +${(upsell.price / 100).toFixed(0)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {t(`pageContent.pricing.upsells.${upsell.id}.description`, '')}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            {selectedUpsells.length > 0 && (
              <div className="mt-4 flex justify-between items-center text-sm font-medium">
                <span className="text-gray-600">{t('pageContent.pricing.upsells.subtotal')}:</span>
                <span className="text-primary-600">+${(getUpsellTotal() / 100).toFixed(0)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="pt-4">
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          className="uppercase font-bold"
          disabled={loading || (files.length === 0 && !(isResubscribeMode && resubscribeData?.hasPdf)) || !email}
        >
          {loading ? (
            <>
              <span className="animate-spin mr-2">⧖</span>
              {t('pageContent.home.uploadSection.form.processing')}
            </>
          ) : (
            <>
              {t('pageContent.home.uploadSection.form.submit')}
              {' · $'}
              {formatDollars(Math.round(getPrice() * (1 - (couponStatus === 'valid' ? discountPercent : 0) / 100)))}
              {t(billingType === 'yearly'
                ? 'pageContent.home.uploadSection.form.perYear'
                : 'pageContent.home.uploadSection.form.perMonth')}
            </>
          )}
        </Button>
        <ul className="mt-3 space-y-1 text-sm text-gray-600">
          {(['trustCancel', 'trust24h', 'trustNoCommission'] as const).map((key) => (
            <li key={key} className="flex items-center justify-center gap-2">
              <span aria-hidden="true" className="text-green-600">✓</span>
              {t(`pageContent.home.uploadSection.form.${key}`)}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-center text-sm text-gray-500">
          {t('pageContent.home.uploadSection.form.paymentNote')}
        </p>
      </div>

      {showPreview !== null && files[showPreview] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
          role="dialog"
          aria-modal="true"
          onKeyDown={(e) => { if (e.key === 'Escape') setShowPreview(null); }}
          tabIndex={-1}
          ref={(node) => { if (node) node.focus(); }}
        >
          <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-medium">{t('pageContent.home.uploadSection.form.pdfPreview')}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClosePreview}
                aria-label={t('pageContent.home.uploadSection.form.closePreview')}
              >
                ✕
              </Button>
            </div>
            <div className="p-4 overflow-auto">
              <PdfPreview file={files[showPreview]} />
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export { Form };